/**
 * Lightweight i18n context for the Botilia search widget.
 *
 * ~1 KB — no external dependencies beyond React.
 */
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getTranslation, interpolate, type TranslationKey, type LangCode } from './index.js';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface I18nContextValue {
  /** Current active language code (e.g. 'el', 'en'). */
  lang: LangCode;
  /**
   * Translate a key, optionally interpolating `{param}` placeholders.
   *
   * @example t('search.resultsTime', { count: 42, time: 12 })
   */
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const I18nContext = createContext<I18nContextValue | null>(null);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

interface I18nProviderProps {
  lang: LangCode;
  children: React.ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ lang, children }) => {
  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>) =>
      interpolate(getTranslation(lang, key), params),
    [lang],
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, t }), [lang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Access the current `lang` and `t()` translation function.
 * Must be used inside an `<I18nProvider>`.
 */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n() must be used inside <I18nProvider>');
  }
  return ctx;
}
